import { useEffect, useRef, useState } from "react";
import api, { getCompany, setCompany } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building, ImagePlus, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ACCENT = { sio: "from-amber-400 to-amber-600", tkc: "from-sky-400 to-sky-600" };

export function useCompanies() {
  const [list, setList] = useState([]);
  const reload = () => api.get("/companies").then(r => setList(r.data));
  useEffect(() => { reload(); }, []);
  return { list, active: list.find(c => c.id === getCompany()), reload };
}

export function CompanyLogo({ company, className = "w-16 h-16", testid }) {
  if (company.logo_url) {
    return (
      <div className={cn("shrink-0 rounded-xl bg-white flex items-center justify-center overflow-hidden p-1", className)} data-testid={testid}>
        <img src={`${BACKEND_URL}${company.logo_url}`} alt={company.name} className="w-full h-full object-contain" />
      </div>
    );
  }
  return (
    <div className={cn("shrink-0 rounded-xl bg-gradient-to-br flex items-center justify-center font-display font-bold text-slate-950", ACCENT[company.id] || ACCENT.sio, className)} data-testid={testid}>
      {company.short}
    </div>
  );
}

export function LogoUploadButton({ companyId, onDone, className, label = "Logo değiştir" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    ref.current.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/companies/${companyId}/logo`, fd);
      toast.success("Logo güncellendi"); onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Logo yüklenemedi"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <input type="file" accept="image/png,image/jpeg,image/webp" ref={ref} className="hidden" onChange={onFile} data-testid={`logo-input-${companyId}`} />
      <button type="button" onClick={(e) => { e.stopPropagation(); ref.current?.click(); }} disabled={busy}
        className={cn("inline-flex items-center gap-1 text-xs transition-colors", className)} data-testid={`logo-upload-${companyId}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} {label}
      </button>
    </>
  );
}

export function CompanySwitcher({ list }) {
  return (
    <Select value={getCompany()} onValueChange={(v) => v !== getCompany() && setCompany(v)}>
      <SelectTrigger className="w-[230px] border-amber-500/40" data-testid="company-switcher">
        <Building className="w-4 h-4 mr-2 text-amber-500 shrink-0" />
        <SelectValue placeholder="Şirket" />
      </SelectTrigger>
      <SelectContent>
        {list.map(c => (
          <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.id}`}>{c.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
